import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Editor from '@monaco-editor/react';
import { codeSnippetAPI } from '../services/api';

const LANGUAGES = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'sql', label: 'SQL' },
  { value: 'csharp', label: 'C#' },
  { value: 'cpp', label: 'C++' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'kotlin', label: 'Kotlin' },
  { value: 'swift', label: 'Swift' }
];

const DIFFICULTIES = ['beginner', 'intermediate', 'advanced', 'expert'];

const CodeSnippetEditor = ({ open, onClose, onSave, snippet = null, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    programmingLanguage: 'javascript',
    code: '',
    tags: [],
    difficulty: 'intermediate',
    isPublished: false,
    generateExplanations: true
  });
  
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editorTheme, setEditorTheme] = useState('vs-dark');
  const [generatingDescription, setGeneratingDescription] = useState(false);

  useEffect(() => {
    if (snippet && mode === 'edit') {
      setFormData({
        name: snippet.name || '',
        description: snippet.description || '',
        programmingLanguage: snippet.programmingLanguage || 'javascript',
        code: snippet.code || '',
        tags: snippet.tags || [],
        difficulty: snippet.difficulty || 'intermediate',
        isPublished: snippet.isPublished || false,
        generateExplanations: false // Don't regenerate by default on edit
      });
    } else {
      // Reset form for create mode
      setFormData({
        name: '',
        description: '',
        programmingLanguage: 'javascript',
        code: '',
        tags: [],
        difficulty: 'intermediate',
        isPublished: false,
        generateExplanations: true
      });
    }
    setError('');
  }, [snippet, mode, open]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCodeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      code: value || ''
    }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleGenerateDescription = async () => {
    try {
      if (!formData.name.trim() || !formData.code.trim()) {
        setError('Please enter a name and code before generating description');
        return;
      }

      setGeneratingDescription(true);
      setError('');

      const response = await codeSnippetAPI.generateDescription({
        name: formData.name,
        code: formData.code,
        programmingLanguage: formData.programmingLanguage
      });

      handleChange('description', response.data.description);
    } catch (err) {
      console.error('Error generating description:', err);
      setError(err.response?.data?.message || 'Failed to generate description');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (!formData.name.trim()) {
        setError('Name is required');
        return;
      }
      if (!formData.code.trim()) {
        setError('Code is required');
        return;
      }

      setSaving(true);
      setError('');

      console.log('Submitting snippet data:', formData);
      await onSave(formData);
      
      // Reset form on successful save
      setFormData({
        name: '',
        description: '',
        programmingLanguage: 'javascript',
        code: '',
        tags: [],
        difficulty: 'intermediate',
        isPublished: false,
        generateExplanations: true
      });
      
      onClose();
    } catch (err) {
      console.error('Error saving snippet:', err);
      console.error('Error response:', err.response);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save snippet';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const editorOptions = {
    minimap: { enabled: false },
    fontSize: 16,
    lineNumbers: 'on',
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on'
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: { 
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: '18px' }}>
            {mode === 'create' ? 'Create Code Snippet' : 'Edit Code Snippet'}
          </h3>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent 
        dividers
        sx={{
          overflow: 'auto',
          flex: 1,
          padding: '16px 24px'
        }}
      >
        {error && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            backgroundColor: '#fff5f5',
            border: '1px solid #feb2b2',
            borderRadius: '8px',
            color: '#c53030',
            fontSize: '13px'
          }}>
            {error}
            <button
              onClick={() => setError('')}
              style={{
                float: 'right',
                background: 'none',
                border: 'none',
                color: '#c53030',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              ×
            </button>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Name */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
              Snippet Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Array Map Function Example"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
          </div>

          {/* Code Editor */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
              Code <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
              <Editor
                height="400px"
                language={formData.programmingLanguage}
                value={formData.code}
                onChange={handleCodeChange}
                theme={editorTheme}
                options={editorOptions}
              />
            </div>
            <div style={{ marginTop: '8px', textAlign: 'right' }}>
              <button
                onClick={() => setEditorTheme(editorTheme === 'vs-dark' ? 'light' : 'vs-dark')}
                className="btn btn-secondary"
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                Toggle Theme
              </button>
            </div>
          </div>

          {/* Language and Difficulty */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                Language <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={formData.programmingLanguage}
                onChange={(e) => handleChange('programmingLanguage', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: 'white'
                }}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                Difficulty
              </label>
              <select
                value={formData.difficulty}
                onChange={(e) => handleChange('difficulty', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  backgroundColor: 'white'
                }}
              >
                {DIFFICULTIES.map(diff => (
                  <option key={diff} value={diff}>
                    {diff.charAt(0).toUpperCase() + diff.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
              Tags
            </label>
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleAddTag}
              placeholder="Type a tag and press Enter"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px'
              }}
            />
            {formData.tags.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#e6fffa',
                      color: '#234e52',
                      borderRadius: '4px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#234e52',
                        cursor: 'pointer',
                        fontSize: '16px',
                        padding: 0,
                        lineHeight: '1'
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Options */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isPublished}
                onChange={(e) => handleChange('isPublished', e.target.checked)}
                style={{ marginTop: '2px' }}
              />
              <span style={{ fontSize: '13px' }}>
                Publish snippet (make visible to users)
              </span>
            </label>

            <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.generateExplanations}
                onChange={(e) => handleChange('generateExplanations', e.target.checked)}
                style={{ marginTop: '2px' }}
              />
              <div>
                <div style={{ fontSize: '13px', marginBottom: '2px' }}>
                  {mode === 'create' ? 'Generate AI explanations on save' : 'Regenerate AI explanations'}
                </div>
                <div style={{ fontSize: '11px', color: '#718096' }}>
                  {formData.generateExplanations 
                    ? 'AI will analyze the code and create line-by-line explanations (may take 10-30 seconds)'
                    : 'Skip AI analysis for now (you can generate explanations later)'
                  }
                </div>
              </div>
            </label>
          </div>

          {/* Description */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '13px', fontWeight: '500' }}>
                Description
              </label>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={generatingDescription || !formData.name.trim() || !formData.code.trim()}
                className="btn btn-secondary"
                style={{
                  fontSize: '12px',
                  padding: '6px 12px',
                  opacity: (generatingDescription || !formData.name.trim() || !formData.code.trim()) ? 0.6 : 1,
                  cursor: (generatingDescription || !formData.name.trim() || !formData.code.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {generatingDescription ? '🔄 Generating...' : '✨ Generate Description'}
              </button>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Brief description of what this code does..."
              rows={3}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
            <div style={{ fontSize: '11px', color: '#718096', marginTop: '4px' }}>
              💡 Use the "Generate Description" button to auto-generate a description based on the code name and content
            </div>
          </div>
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <button
          onClick={onClose}
          disabled={saving}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || !formData.name.trim() || !formData.code.trim()}
          className="btn btn-primary"
        >
          {saving ? (formData.generateExplanations ? 'Saving & Analyzing...' : 'Saving...') : 'Save Snippet'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default CodeSnippetEditor;
